import type { ReactNode } from 'react';
import { addSeconds, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { useCreditSpend, useCreditSummary } from 'features/credits/hooks/use-credits';
import { seatsLabel } from 'features/iam/components/seats/plan-actions';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';
import { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { seatUsageMeters } from 'features/usage/utils/seat-usage-meters';
import { formatPercent } from 'features/usage/utils/usage-format';
import { UsageBar } from './usage-bar';
import { formatCredits, formatCreditValue } from './usage-credits';

const percentOf = (used: number, total: number) => (total > 0 ? (used / total) * 100 : 0);

const creditsMeter = (meters: SubscriptionUsageMeter[]) =>
  meters.find((meter) => meter.meterKey === 'ai-credits' && !meter.userId);

/** A bar with its percentage at the right. */
const MeterBar = ({ percent }: { percent: number }) => (
  <div className="flex items-center gap-5">
    <UsageBar percent={percent} />
    <span className="shrink-0 text-xs font-medium text-grey-800 dark:text-muted-foreground">
      {formatPercent(percent, 2)}
    </span>
  </div>
);

/** One plan or the shared credits: its name and terms on the left, what was used on the right. */
const CreditsRow = ({ name, detail, children }: { name: string; detail: string; children: ReactNode }) => (
  <li className="flex flex-col gap-3 py-3.5 sm:flex-row sm:items-center sm:gap-6">
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-foreground">{name}</p>
      <p className="text-xs text-muted-foreground">{detail}</p>
    </div>
    <div className="w-full sm:w-1/3 sm:shrink-0">{children}</div>
  </li>
);

const UsedOf = ({ used, total }: { used: number; total: number }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1">
      <p className="flex items-baseline gap-1">
        <span className="text-sm text-grey-900 dark:text-foreground">{formatCreditValue(used)}</span>
        <span className="text-xs text-muted-foreground">
          {t('USAGE_OF_TOTAL_USED', { total: formatCreditValue(total), defaultValue: 'of {{total}} used' })}
        </span>
      </p>
      <MeterBar percent={percentOf(used, total)} />
    </div>
  );
};

/** The organization's credits this period: every plan's seats added up, then the shared top-up credits. */
export function UsageOrgCredits() {
  const { t } = useTranslation();
  const { planSeats, isLoading } = usePlanSeats();
  const { data: summary } = useCreditSummary();
  const { data: spend } = useCreditSpend(true);

  if (isLoading) return null;

  const heldPlans = planSeats.filter((entry) => entry.pool && !entry.awaitingPayment);
  const pools = planSeats.flatMap((entry) => (entry.pool ? [entry.pool] : []));
  const total = creditsMeter(seatUsageMeters(pools));
  // A period ending at 23:59:59 renews the next day; one ending at midnight renews that day.
  const renewsOn = total?.periodEndUtc
    ? format(addSeconds(new Date(total.periodEndUtc), 1), 'MMM d, yyyy')
    : '';

  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none">
      <CardContent className="flex flex-col p-5">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
          <h2 className="text-base font-medium leading-heading text-foreground">
            {t('USAGE_CREDITS_THIS_MONTH', 'Credits this month')}
          </h2>
          {renewsOn && (
            <p className="text-xs text-muted-foreground">
              {t('USAGE_PLAN_CREDITS_RENEW_ON', {
                date: renewsOn,
                defaultValue: 'Plan credits renew on {{date}}',
              })}
            </p>
          )}
        </div>

        {total && (
          <div className="flex flex-col gap-2.5 pb-4 pt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p className="flex flex-wrap items-baseline gap-2">
                <span className="text-2xl font-medium leading-heading text-foreground sm:text-3xl sm:leading-heading">
                  {formatCreditValue(total.used)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t('USAGE_OF_PLAN_CREDITS_USED', {
                    total: formatCreditValue(total.included),
                    defaultValue: 'of {{total}} plan credits used',
                  })}
                </span>
              </p>
              <span className="text-sm text-grey-600 dark:text-muted-foreground">
                {t('USAGE_CREDITS_LEFT_COUNT', {
                  credits: formatCreditValue(total.remaining),
                  defaultValue: '{{credits}} left',
                })}
              </span>
            </div>
            <MeterBar percent={percentOf(total.used, total.included)} />
          </div>
        )}

        <ul className="divide-y divide-border border-t border-border">
          {/* Only plans the organization holds; one not bought or still awaiting payment has no credits. */}
          {heldPlans.map((entry) => {
            const meter = entry.pool ? creditsMeter(seatUsageMeters([entry.pool])) : undefined;
            let detail = seatsLabel(t, entry.purchased);
            if (meter && entry.purchased > 0) {
              detail = t('USAGE_SEATS_TIMES_CREDITS', {
                seats: seatsLabel(t, entry.purchased),
                credits: formatCreditValue(meter.included / entry.purchased),
                unit: meter.unitLabel,
                defaultValue: '{{seats}} × {{credits}} {{unit}}',
              });
              if (entry.freeThisPeriod > 0) {
                detail += ` · ${t('USAGE_SEATS_NOT_ASSIGNED', {
                  count: entry.freeThisPeriod,
                  defaultValue: '{{count}} not assigned',
                })}`;
              }
            }

            return (
              <CreditsRow key={entry.code} name={entry.name} detail={detail}>
                {meter ? (
                  <UsedOf used={meter.used} total={meter.included} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t('USAGE_NO_CREDITS', 'No credits')}</p>
                )}
              </CreditsRow>
            );
          })}

          {summary?.enabled && (
            <CreditsRow
              name={t('USAGE_SHARED_CREDITS', 'Shared credits')}
              detail={t('USAGE_SHARED_CREDITS_ROW_NOTE', "Never expire. Used when someone's plan runs out")}
            >
              {/* No API reports the total bought, so there is no "of N" or bar here: only what is left and what was spent. */}
              <p className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
                <span className="text-grey-900 dark:text-foreground">
                  {t('USAGE_CREDITS_LEFT_COUNT', {
                    credits: formatCredits(summary.balanceCredits),
                    defaultValue: '{{credits}} left',
                  })}
                </span>
                {spend && (
                  <span className="text-xs text-muted-foreground">
                    {t('USAGE_CREDITS_USED_COUNT', {
                      credits: formatCredits(spend.lifetimeSpentCredits),
                      defaultValue: '{{credits}} used',
                    })}
                  </span>
                )}
              </p>
            </CreditsRow>
          )}
        </ul>
      </CardContent>
    </Card>
  );
}
